import { useCallback, useRef, useState, useEffect } from 'react';
import { PCMStreamPlayer } from '../audio/PCMStreamPlayer';

/**
 * Resample audio from source sample rate to target sample rate using linear interpolation.
 * This is the standard approach for real-time audio processing in web browsers.
 */
function resampleAudio(
  sourceBuffer: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number
): Float32Array {
  if (sourceSampleRate === targetSampleRate) {
    return sourceBuffer;
  }

  const sampleRateRatio = sourceSampleRate / targetSampleRate;
  const newLength = Math.round(sourceBuffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);

  for (let i = 0; i < newLength; i++) {
    const sourceIndex = i * sampleRateRatio;
    const sourceIndexFloor = Math.floor(sourceIndex);
    const sourceIndexCeil = Math.min(sourceIndexFloor + 1, sourceBuffer.length - 1);
    const interpolation = sourceIndex - sourceIndexFloor;

    result[i] = sourceBuffer[sourceIndexFloor] * (1 - interpolation) +
                sourceBuffer[sourceIndexCeil] * interpolation;
  }

  return result;
}

interface UseVoiceLinkV2Options {
  language: string;
  projectId?: string;
  entityType: string;
  entityId: string;
  entityName: string;
  fieldConfig: Record<string, string>;
  fieldLabels: Record<string, { label: string; description: string }>;
  kbId?: string;
  kbName?: string;
  projectName?: string;
  documentIds?: string[];
  solutionId?: string;
  activeField?: string | null;
  onTranscript?: (text: string, isFinal: boolean) => void;
  onToken?: (content: string) => void;
  onResponseComplete?: (content: string) => void;
  onAction?: (action: string, data: unknown) => void;
  onFieldUpdated?: (fieldName: string, value: unknown) => void;
  onFieldRead?: (fieldName: string) => void;
  onMetrics?: (metrics: PerformanceMetrics) => void;
  onError?: (error: string) => void;
}

export interface PerformanceMetrics {
  turn_id: string;
  stt_duration_s: number;
  llm_total_s: number;
  llm_ttft_s: number;
  llm_tokens: number;
  llm_tokens_per_s: number;
  llm_input_tokens: number;
  llm_context_window_size: number;
  tts_total_s: number;
  tts_avg_ttfc_s: number;
  total_turn_s: number;
  time_to_first_audio_s: number;
  stt_pct: number;
  llm_pct: number;
  tts_pct: number;
  sentence_count: number;
}

export const useVoiceLinkV2 = ({
  language,
  projectId,
  entityType,
  entityId,
  entityName,
  fieldConfig,
  fieldLabels,
  kbId,
  kbName,
  projectName,
  documentIds,
  solutionId,
  activeField,
  onTranscript,
  onToken,
  onResponseComplete,
  onAction,
  onFieldUpdated,
  onFieldRead,
  onMetrics,
  onError,
}: UseVoiceLinkV2Options) => {
  const [isReady, setIsReady] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const isRecordingRef = useRef(false);
  const isPlayingRef = useRef(false);
  // TTS playback via PCMStreamPlayer (Kyutai 24 kHz PCM)
  const pcmPlayerRef = useRef<PCMStreamPlayer | null>(null);
  
  // Refs for values needed in connect() to avoid stale closures
  const solutionIdRef = useRef(solutionId);
  const activeFieldRef = useRef(activeField);
  useEffect(() => { solutionIdRef.current = solutionId; }, [solutionId]);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  // Callback refs to avoid stale closures
  const onTranscriptRef = useRef(onTranscript);
  const onTokenRef = useRef(onToken);
  const onResponseCompleteRef = useRef(onResponseComplete);
  const onActionRef = useRef(onAction);
  const onFieldUpdatedRef = useRef(onFieldUpdated);
  const onFieldReadRef = useRef(onFieldRead);
  const onMetricsRef = useRef(onMetrics);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);
  useEffect(() => { onResponseCompleteRef.current = onResponseComplete; }, [onResponseComplete]);
  useEffect(() => { onActionRef.current = onAction; }, [onAction]);
  useEffect(() => { onFieldUpdatedRef.current = onFieldUpdated; }, [onFieldUpdated]);
  useEffect(() => { onFieldReadRef.current = onFieldRead; }, [onFieldRead]);
  useEffect(() => { onMetricsRef.current = onMetrics; }, [onMetrics]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    console.log('[VoiceLinkV2] VERSION: 2026-06-01-pcm-streaming');
  }, []);

  // Enumerate available audio input devices
  useEffect(() => {
    const updateDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        setAvailableDevices(audioInputs);
        if (audioInputs.length > 0 && !selectedDeviceId) {
          // Try to find PC-LM1E microphone first
          const preferredDevice = audioInputs.find(d => d.label.includes('PC-LM1E'));
          setSelectedDeviceId(preferredDevice ? preferredDevice.deviceId : audioInputs[0].deviceId);
        }
      } catch (e) {
        console.error('[VoiceLinkV2] Failed to enumerate devices:', e);
      }
    };
    updateDevices();
    navigator.mediaDevices.addEventListener('devicechange', updateDevices);
    return () => navigator.mediaDevices.removeEventListener('devicechange', updateDevices);
  }, [selectedDeviceId]);

  // Resume PCM player on tab visibility change (AudioContext may be suspended)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        pcmPlayerRef.current?.resume();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const handleInterruption = useCallback(() => {
    pcmPlayerRef.current?.flush();
    isPlayingRef.current = false;
    setIsPlaying(false);
  }, []);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    setIsRecording(false);
    setAudioLevel(0);

    // Signal backend to transcribe the buffered audio BEFORE tearing down the processor
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[VoiceLinkV2] 📤 Sending audio_end to trigger STT transcription');
      wsRef.current.send(JSON.stringify({ type: 'audio_end' }));
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    console.log('[VoiceLinkV2] Recording stopped');
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecordingRef.current) {
      console.log('[VoiceLinkV2] Already recording');
      return;
    }

    // Check WebSocket connection before starting recording
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[VoiceLinkV2] ❌ Cannot start recording: WebSocket not connected');
      onError?.('Connection not ready. Please wait a moment and try again.');
      return;
    }

    // Clean up any existing audio resources (defensive programming)
    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch (e) {
        console.warn('[VoiceLinkV2] Error disconnecting old processor:', e);
      }
      processorRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        await audioContextRef.current.close();
      } catch (e) {
        console.warn('[VoiceLinkV2] Error closing old audio context:', e);
      }
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Initialize/resume PCM player on user gesture (required by Safari/Chrome autoplay policy)
    if (!pcmPlayerRef.current) {
      pcmPlayerRef.current = new PCMStreamPlayer((playing) => {
        isPlayingRef.current = playing;
        setIsPlaying(playing);
        console.log(`[VOICE_WS t=${Math.round(performance.now())}ms isPlaying=${playing}]`);
      });
    }
    await pcmPlayerRef.current.start();

    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {}),
        },
      };
      
      console.log('[VoiceLinkV2] Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Use native sample rate, then resample to 16kHz
      const audioContext = new (window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
      const sourceSampleRate = audioContext.sampleRate;
      const targetSampleRate = 16000;
      console.log('[VoiceLinkV2] AudioContext:', sourceSampleRate, 'Hz → resampling to', targetSampleRate, 'Hz');
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      
      processor.onaudioprocess = (e) => {
        if (!isRecordingRef.current) return;

        const inputData = e.inputBuffer.getChannelData(0);
        
        // Calculate audio level from original data
        let sum = 0;
        for (let i = 0; i < inputData.length; i++) {
          sum += inputData[i] * inputData[i];
        }
        const rms = Math.sqrt(sum / inputData.length);
        const level = Math.min(100, rms * 300);
        setAudioLevel(level);

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          // Resample to 16kHz before sending
          const resampledData = resampleAudio(inputData, sourceSampleRate, targetSampleRate);
          const pcmData = new Int16Array(resampledData.length);
          for (let i = 0; i < resampledData.length; i++) {
            pcmData[i] = Math.max(-1, Math.min(1, resampledData[i])) * 0x7FFF;
          }
          wsRef.current.send(pcmData.buffer);
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      
      isRecordingRef.current = true;
      setIsRecording(true);
    } catch (e) {
      console.error('[VoiceLinkV2] ❌ Recording failed:', e);
      
      // Clean up on failure
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch { /* ignore audio context close error */ }
        audioContextRef.current = null;
      }
      if (processorRef.current) {
        try {
          processorRef.current.disconnect();
        } catch { /* ignore processor disconnect error */ }
        processorRef.current = null;
      }
      
      // Provide user-friendly error message
      let errorMessage = 'Microphone access failed';
      if (e instanceof Error) {
        if (e.name === 'NotAllowedError') {
          errorMessage = 'Microphone permission denied. Please allow microphone access in your browser settings.';
        } else if (e.name === 'NotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
        } else if (e.name === 'NotReadableError') {
          errorMessage = 'Microphone is busy or unavailable. Please close other applications using it.';
        } else {
          errorMessage = e.message || errorMessage;
        }
      }
      onError?.(errorMessage);
    }
  }, [onError, selectedDeviceId]);

  const connect = useCallback(() => {
    
    // Guard against multiple simultaneous connection attempts
    if (wsRef.current) {
      console.log('[VoiceLinkV2] WebSocket already exists, state:', wsRef.current.readyState);
      if (wsRef.current.readyState === WebSocket.CONNECTING) {
        console.log('[VoiceLinkV2] Connection already in progress, skipping...');
        return;
      }
      if (wsRef.current.readyState === WebSocket.OPEN) {
        console.log('[VoiceLinkV2] Already connected! Setting isReady=true for remounted component');
        if (isMountedRef.current) {
          setIsReady(true);
        }
        return;
      }
      if (wsRef.current.readyState !== WebSocket.CLOSED) {
        console.log('[VoiceLinkV2] Closing previous connection...');
        wsRef.current.close();
      }
    }

    const token = sessionStorage.getItem('access_token');
    if (!token) {
      console.error('[VoiceLinkV2] No authentication token found');
      onError?.('No authentication token found');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    let host = window.location.host;
    if (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.startsWith('http')) {
      try {
        const url = new URL(import.meta.env.VITE_API_BASE_URL);
        host = url.host;
      } catch {
        console.warn('[VoiceLinkV2] Invalid VITE_API_BASE_URL, falling back to window.location.host');
      }
    } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      if (window.location.port !== '8001') {
        console.log('[VoiceLinkV2] Local dev: Redirecting WebSocket to direct backend port 8001');
        host = `${window.location.hostname}:8001`;
      }
    }
    
    // V2 endpoint - NO token in URL (First Message Auth)
    const wsUrl = `${protocol}//${host}/api/speaking-chatbot/v2/ws?language=${language}${projectId ? `&project_id=${projectId}` : ''}${kbId ? `&kb_id=${kbId}` : ''}`;

    setIsConnecting(true);
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer'; // receive binary as ArrayBuffer (synchronous, no race condition)
    wsRef.current = ws;

    ws.onopen = () => {
      // First Message Auth: Send token as first message
      // Use refs for solutionId/activeField to avoid stale closure
      const currentSolutionId = solutionIdRef.current;
      const currentActiveField = activeFieldRef.current;
      ws.send(JSON.stringify({
        type: 'auth',
        token: token,
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        field_config: fieldConfig,
        field_labels: fieldLabels,
        project_name: projectName,
        kb_name: kbName,
        document_ids: documentIds,
        ...(currentSolutionId ? { solution_id: currentSolutionId } : {}),
        ...(currentActiveField ? { active_field: currentActiveField } : {}),
      }));
    };

    ws.onmessage = (event) => {
      // Binary frame = raw PCM int16 samples from Kyutai TTS
      if (event.data instanceof ArrayBuffer) {
        pcmPlayerRef.current?.push(event.data);
        return;
      }

      try {
        const msg = JSON.parse(event.data);
        if (msg.type !== 'ping' && msg.type !== 'pong') {
          console.log(`[VOICE_WS t=${Math.round(performance.now())}ms type=${msg.type}]`);
        }
        switch (msg.type) {
          case 'ready':
            if (isMountedRef.current) {
              setIsReady(true);
              setIsConnecting(false);
            }
            // Start keep-alive ping every 30s to prevent backend idle timeout
            if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = window.setInterval(() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
              }
            }, 30000);
            break;
          case 'auth_error':
            onErrorRef.current?.(msg.content || 'Authentication failed');
            if (isMountedRef.current) {
              setIsConnecting(false);
            }
            ws.close(4401, 'Auth failed');
            break;
          case 'stt_transcript':
            if (!msg.is_final && isPlayingRef.current) {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'interruption' }));
              }
              handleInterruption();
            }
            onTranscriptRef.current?.(msg.text, msg.is_final);
            break;
          case 'token':
            onTokenRef.current?.(msg.content);
            break;
          case 'tts_done':
            // Signal the AudioWorklet that no more PCM data will arrive.
            // The worklet will keep playing buffered audio and only signal
            // playing:false once the ring buffer is fully drained.
            // Do NOT flush — that would discard unplayed audio.
            pcmPlayerRef.current?.drain();
            break;
          case 'response_complete':
            onResponseCompleteRef.current?.(msg.content);
            break;
          case 'pdm_interruption':
            handleInterruption();
            break;
          case 'vad_speech_end':
            isRecordingRef.current = false;
            setIsRecording(false);
            setAudioLevel(0);
            if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
            if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
            if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
            break;
          case 'workspace_action':
            onActionRef.current?.(msg.action, msg.data);
            break;
          case 'tool_executing':
            onActionRef.current?.('tool_executing', { count: msg.count });
            break;
          case 'context_field_changed':
            onActionRef.current?.('context_field_changed', { target_field: msg.target_field });
            break;
          
          // SCV2 specific message types
          case 'field_updated':
            onFieldUpdatedRef.current?.(msg.field_name, msg.value);
            break;
          case 'field_read':
            onFieldReadRef.current?.(msg.field_name);
            break;
          case 'project_updated':
            // Legacy support - map to field_updated
            onFieldUpdatedRef.current?.(msg.field, msg.value);
            break;
          case 'performance_metrics':
            onMetricsRef.current?.(msg.metrics);
            break;
            
          case 'error':
            onErrorRef.current?.(msg.content);
            break;
          case 'pong':
            break;
        }
      } catch (e) {
        console.error('[VoiceLinkV2] Parse error:', e);
      }
    };

    ws.onclose = (event) => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (isMountedRef.current) {
        setIsReady(false);
        setIsConnecting(false);
        setIsRecording(false);
      }
      
      isRecordingRef.current = false;
      wsRef.current = null;
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      
      if (event.code === 1011) {
        onErrorRef.current?.(`Connection closed: ${event.reason || 'Service initialization failed'}`);
      } else if (event.code !== 1000 && event.code !== 4401) {
        // Unexpected close: silently reconnect so next message works.
        // Use 2 s delay (was 1 s) so any in-flight server-side orchestrator has
        // time to finish closing before the new auth message arrives, preventing
        // two orchestrators from coexisting and producing duplicate messages.
        if (isMountedRef.current) {
          console.log('[VoiceLinkV2] Unexpected close (code', event.code, '), auto-reconnecting in 2s...');
          setTimeout(() => {
            if (isMountedRef.current && !wsRef.current) {
              connect();
            }
          }, 2000);
        }
      }
    };

    ws.onerror = () => {
      if (isMountedRef.current) {
        setIsConnecting(false);
        setIsReady(false);
      }
      onErrorRef.current?.('WebSocket connection failed');
    };
  }, [language, projectId, kbId, kbName, projectName, documentIds, handleInterruption]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      // Stop PCM player
      pcmPlayerRef.current?.stop();
      pcmPlayerRef.current = null;
      // Close WebSocket on unmount regardless of state (OPEN or CONNECTING).
      // Previously a CONNECTING socket was left dangling, which under React
      // StrictMode (mount→unmount→mount) caused two sockets to both reach OPEN
      // and authenticate, creating two server-side orchestrators that each
      // streamed tokens and produced duplicate assistant messages.
      if (wsRef.current) {
        const state = wsRef.current.readyState;
        if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
          try { wsRef.current.close(1000, 'Component unmount'); } catch { /* ignore close error on unmount */ }
        }
        wsRef.current = null;
      }
    };
  }, []);

  const disconnect = useCallback(() => {
    stopRecording();
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmount');
      wsRef.current = null;
    }
    setIsReady(false);
    setIsConnecting(false);
  }, [stopRecording]);

  // Send active UI state (tab and field) to backend
  const sendActiveState = useCallback((activeTab: string, activeField: string | null) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'active_state',
        active_tab: activeTab,
        active_field: activeField,
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Send a text message (for typed input)
  const sendTextMessage = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'text_message',
        content: text,
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Send field value back to backend (response to read_field tool)
  const sendFieldValue = useCallback((fieldName: string, value: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'field_value',
        field_name: fieldName,
        value: value,
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Update document IDs dynamically (for document selection changes)
  const updateDocumentIds = useCallback((documentIds: string[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'update_context',
        document_ids: documentIds,
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Send interruption signal
  const interrupt = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'interruption' }));
    }
  }, []);

  return {
    isReady,
    isConnecting,
    isRecording,
    isPlaying,
    audioLevel,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    availableDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    interrupt,
    // V2 specific
    sendActiveState,
    sendTextMessage,
    sendFieldValue,
    updateDocumentIds,
  };
};
